import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CompaniesSettingsDialogComponent } from 'src/app/companies/companies-settings-dialog/companies-settings-dialog.component';
import { AllowedDeductionType, Company } from 'src/app/core/companies.service';
import { DialogService } from 'src/app/core/dialog.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { UtilsService } from 'src/app/core/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesSubdomainDialogComponent } from 'src/app/companies/companies-subdomain-dialog/companies-subdomain-dialog.component';
import { LongRange } from 'src/app/core/long-range.service';
import { CompaniesAllowedStepsDialogComponent } from 'src/app/companies/companies-allowed-steps-dialog/companies-allowed-steps-dialog.component';
import { CompaniesTicketDialogComponent } from 'src/app/companies/companies-ticket-dialog/companies-ticket-dialog.component';

@Component({
  selector: 'app-company-details-general-settings',
  templateUrl: './company-details-general-settings.component.html',
  styleUrls: ['./company-details-general-settings.component.scss'],
})
export class CompanyDetailsGeneralSettingsComponent {
  @Input() company?: Company;
  @Input() company_long_ranges?: LongRange[];
  @Input() default_long_ranges?: LongRange[];
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();

  displayed_columns: string[] = ['amount_range', 'allowed_steps'];

  constructor(
    private utils_service: UtilsService,
    private dialog_service: DialogService,
    private error_service: ErrorService,

    private translate: TranslateService,
  ) {}

  getCompanySettingsBackgroundClass(settings: boolean): { [key: string]: boolean } {
    return this.utils_service.getCompanySettingsBackgroundClass(settings);
  }

  onSettingsClick(): void {
    this.dialog_service
      .openDialog(CompaniesSettingsDialogComponent, { company: this.company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetCustomDomainClick(): void {
    this.dialog_service
      .openDialog(CompaniesSubdomainDialogComponent, { company_id: this.company?.company_id })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onAllowedStepsClick(): void {
    this.dialog_service
      .openDialog(CompaniesAllowedStepsDialogComponent, { company: this.company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onTicketClick(): void {
    this.dialog_service
      .openDialog(CompaniesTicketDialogComponent, { company: this.company })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  getDeductionsString(): string {
    const deduction_types = this.company?.settings?.allowed_deduction_types;

    if (!deduction_types || deduction_types.length === 0) {
      return '-';
    }

    const full_string = deduction_types
      .map((type: AllowedDeductionType) => {
        const translationKey = this.utils_service.getDeductionTypeLabel(type);

        return this.translate.instant(translationKey);
      })
      .join(', ');

    if (full_string.length > 30) {
      return `${full_string.substring(0, 27)}...`;
    }

    return full_string;
  }

  getLongRangeLabel(long_range: LongRange): { min_label: number; max_label: number } {
    return this.utils_service.getLongRangeLabel(long_range);
  }

  isChecked(step: number, default_long_range: LongRange): boolean {
    const company_long_range = this.company?.long_ranges?.find(
      slr => slr.min_amount === default_long_range.min_amount && slr.max_amount === default_long_range.max_amount,
    );

    if (company_long_range) {
      return company_long_range.allowed_steps?.includes(step) || false;
    }

    return false;
  }
}
